import { useState, useEffect, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { Globe, Server, CreditCard, Users, Settings, LayoutDashboard, LogOut, Plus, Pencil, Trash2, AlertTriangle, Clock, Menu, X, Bell, Smartphone, Download } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// PWA Install Prompt
let deferredPrompt = null;

// Auth Context
const AuthContext = createContext(null);

const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const res = await axios.get(`${API}/auth/me`);
      setUser(res.data);
    } catch (e) {
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    localStorage.setItem('token', res.data.token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${res.data.token}`;
    setToken(res.data.token);
    setUser(res.data.user);
    return res.data;
  };

  const register = async (name, email, password) => {
    const res = await axios.post(`${API}/auth/register`, { name, email, password });
    localStorage.setItem('token', res.data.token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${res.data.token}`;
    setToken(res.data.token);
    setUser(res.data.user);
    return res.data;
  };

  const logout = () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="auth-container">
        <div className="neo-card">
          <p className="font-bold">LOADING...</p>
        </div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// PWA Install Banner Component
const PWAInstallBanner = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    // Check if iOS
    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent);
    setIsIOS(isIOSDevice);

    // Check if already installed
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
    if (isStandalone) return;

    // Listen for install prompt
    const handler = (e) => {
      e.preventDefault();
      deferredPrompt = e;
      setShowBanner(true);
    };

    window.addEventListener('beforeinstallprompt', handler);

    // Show banner for iOS after delay
    if (isIOSDevice && !isStandalone) {
      setTimeout(() => setShowBanner(true), 3000);
    }

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        toast.success('Application installée!');
      }
      deferredPrompt = null;
      setShowBanner(false);
    }
  };

  if (!showBanner) return null;

  return (
    <div className="fixed bottom-4 left-4 right-4 z-50 neo-card p-4 flex items-center justify-between gap-4" data-testid="pwa-install-banner">
      <div className="flex items-center gap-3">
        <Smartphone size={24} className="text-[#FF00FF]" />
        <div>
          <p className="font-bold text-sm">INSTALLER L'APP</p>
          <p className="text-xs opacity-70">
            {isIOS ? 'Tapez sur Partager puis "Sur l\'écran d\'accueil"' : 'Accédez hors ligne et recevez des notifications'}
          </p>
        </div>
      </div>
      <div className="flex gap-2">
        {!isIOS && (
          <button onClick={handleInstall} className="btn-primary px-3 py-2 text-sm flex items-center gap-1" data-testid="install-pwa-btn">
            <Download size={16} /> INSTALLER
          </button>
        )}
        <button onClick={() => setShowBanner(false)} className="btn-secondary p-2" data-testid="close-pwa-banner">
          <X size={16} />
        </button>
      </div>
    </div>
  );
};

// Notification Permission Component
const NotificationPermission = () => {
  const [permission, setPermission] = useState(Notification.permission);
  const [showPrompt, setShowPrompt] = useState(false);

  useEffect(() => {
    if ('Notification' in window && permission === 'default') {
      setTimeout(() => setShowPrompt(true), 5000);
    }
  }, [permission]);

  const requestPermission = async () => {
    try {
      const result = await Notification.requestPermission();
      setPermission(result);
      if (result === 'granted') {
        toast.success('Notifications activées!');
        // Register service worker for push notifications
        if ('serviceWorker' in navigator) {
          const registration = await navigator.serviceWorker.ready;
          console.log('Service Worker ready for notifications', registration);
        }
      }
      setShowPrompt(false);
    } catch (err) {
      console.error('Notification permission error:', err);
    }
  };

  if (!showPrompt || permission !== 'default') return null;

  return (
    <div className="fixed top-4 left-4 right-4 z-50 neo-card p-4 flex items-center justify-between gap-4" data-testid="notification-prompt">
      <div className="flex items-center gap-3">
        <Bell size={24} className="text-[#FF00FF]" />
        <div>
          <p className="font-bold text-sm">ACTIVER LES NOTIFICATIONS</p>
          <p className="text-xs opacity-70">Recevez des alertes avant expiration de vos domaines</p>
        </div>
      </div>
      <div className="flex gap-2">
        <button onClick={requestPermission} className="btn-primary px-3 py-2 text-sm" data-testid="enable-notifications-btn">
          ACTIVER
        </button>
        <button onClick={() => setShowPrompt(false)} className="btn-secondary p-2">
          <X size={16} />
        </button>
      </div>
    </div>
  );
};

// Login Page
const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isRegister, setIsRegister] = useState(false);
  const [name, setName] = useState('');
  const { login, register, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) navigate('/dashboard');
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (isRegister) {
        await register(name, email, password);
        toast.success('COMPTE CRÉÉ!');
      } else {
        await login(email, password);
        toast.success('CONNEXION RÉUSSIE!');
      }
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  return (
    <div className="auth-container" data-testid="auth-page">
      <div className="neo-card" style={{ maxWidth: '450px', width: '100%' }}>
        <h1 className="text-3xl font-bold mb-2 uppercase tracking-wider">SubManager</h1>
        <p className="mb-6 text-sm opacity-70">Gestion de domaines & abonnements</p>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          {isRegister && (
            <div>
              <label className="block font-bold uppercase text-sm mb-1">Nom</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full p-3 border-2 border-black bg-white"
                required
                data-testid="register-name-input"
              />
            </div>
          )}
          <div>
            <label className="block font-bold uppercase text-sm mb-1">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full p-3 border-2 border-black bg-white"
              required
              data-testid="email-input"
            />
          </div>
          <div>
            <label className="block font-bold uppercase text-sm mb-1">Mot de passe</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full p-3 border-2 border-black bg-white"
              required
              data-testid="password-input"
            />
          </div>
          <button type="submit" className="btn-primary w-full p-3" data-testid="submit-btn">
            {isRegister ? 'CRÉER UN COMPTE' : 'SE CONNECTER'}
          </button>
        </form>
        
        <button
          onClick={() => setIsRegister(!isRegister)}
          className="mt-4 text-sm underline w-full text-center"
          data-testid="toggle-auth-mode"
        >
          {isRegister ? 'Déjà un compte? Se connecter' : 'Pas de compte? Créer un compte'}
        </button>
      </div>
    </div>
  );
};

// Sidebar Component
const Sidebar = ({ mobileOpen, setMobileOpen }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();

  const links = [
    { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/domains', icon: Globe, label: 'Domaines' },
    { path: '/hostings', icon: Server, label: 'Hébergements' },
    { path: '/subscriptions', icon: CreditCard, label: 'Abonnements' },
    { path: '/clients', icon: Users, label: 'Clients' },
    { path: '/settings', icon: Settings, label: 'Paramètres' },
  ];

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <>
      {/* Mobile overlay */}
      {mobileOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}
      
      <aside className={`sidebar fixed lg:relative z-50 h-full lg:h-auto transform lg:transform-none transition-transform duration-200 ${mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`} data-testid="sidebar">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold uppercase tracking-wider">SubManager</h2>
          <button className="lg:hidden" onClick={() => setMobileOpen(false)}>
            <X size={24} />
          </button>
        </div>
        
        <nav className="flex-1 space-y-2">
          {links.map((link) => (
            <button
              key={link.path}
              onClick={() => {
                navigate(link.path);
                setMobileOpen(false);
              }}
              className={`sidebar-link w-full ${location.pathname === link.path ? 'active' : ''}`}
              data-testid={`nav-${link.path.slice(1)}`}
            >
              <link.icon size={20} />
              <span>{link.label}</span>
            </button>
          ))}
        </nav>
        
        <button
          onClick={handleLogout}
          className="sidebar-link w-full mt-auto text-red-600"
          data-testid="logout-btn"
        >
          <LogOut size={20} />
          <span>Déconnexion</span>
        </button>
      </aside>
    </>
  );
};

// Dashboard Layout
const DashboardLayout = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <div className="dashboard-layout">
      <Sidebar mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} />
      <main className="main-content">
        <button 
          className="lg:hidden btn-secondary p-2 mb-4"
          onClick={() => setMobileOpen(true)}
          data-testid="mobile-menu-btn"
        >
          <Menu size={24} />
        </button>
        {children}
      </main>
    </div>
  );
};

// Stats Card
const StatCard = ({ title, value, icon: Icon, color }) => (
  <div className="neo-card" data-testid={`stat-${title.toLowerCase()}`}>
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm font-bold uppercase opacity-70">{title}</p>
        <p className="text-4xl font-bold mt-1">{value}</p>
      </div>
      <div className={`p-3 border-2 border-black ${color}`}>
        <Icon size={24} />
      </div>
    </div>
  </div>
);

// Alert Item
const AlertItem = ({ item, type }) => {
  const getBadgeClass = () => {
    if (item.days_until_expiry <= 0) return 'badge-critical';
    if (item.days_until_expiry <= 7) return 'badge-urgent';
    if (item.days_until_expiry <= 15) return 'badge-warning';
    return 'badge-safe';
  };

  const getTypeLabel = () => {
    switch(item.type) {
      case 'domain': return 'Domaine';
      case 'hosting': return 'Hébergement';
      case 'subscription': return 'Abonnement';
      default: return item.type;
    }
  };

  return (
    <div className={`flex items-center justify-between p-3 bg-white border-2 border-black ${item.days_until_expiry <= 0 ? 'pulse-alert' : ''}`} data-testid="alert-item">
      <div className="flex items-center gap-3">
        <span className={getBadgeClass()}>{getTypeLabel()}</span>
        <span className="font-bold">{item.name}</span>
      </div>
      <div className="flex items-center gap-2">
        {item.days_until_expiry <= 0 ? (
          <span className="text-red-600 font-bold">EXPIRÉ!</span>
        ) : (
          <span className="font-mono">{item.days_until_expiry}j restants</span>
        )}
      </div>
    </div>
  );
};

// Dashboard Page
const DashboardPage = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API}/dashboard/stats`);
      setStats(res.data);
    } catch (err) {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="neo-card"><p className="font-bold">CHARGEMENT...</p></div>;
  }

  const allExpiring = [...(stats?.expiring?.critical || []), ...(stats?.expiring?.urgent || []), ...(stats?.expiring?.warning || []), ...(stats?.expiring?.upcoming || [])];

  return (
    <div data-testid="dashboard-page">
      <h1 className="text-3xl font-bold mb-6 uppercase tracking-wider">Dashboard</h1>
      
      {/* Stats Grid */}
      <div className="stats-grid">
        <StatCard title="Domaines" value={stats?.counts?.domains || 0} icon={Globe} color="bg-blue-400" />
        <StatCard title="Hébergements" value={stats?.counts?.hostings || 0} icon={Server} color="bg-green-400" />
        <StatCard title="Abonnements" value={stats?.counts?.subscriptions || 0} icon={CreditCard} color="bg-purple-400" />
        <StatCard title="Clients" value={stats?.counts?.clients || 0} icon={Users} color="bg-orange-400" />
      </div>

      {/* Critical Alerts */}
      {stats?.expiring?.critical?.length > 0 && (
        <div className="alert-section">
          <div className="hazard-stripes p-3 border-4 border-black mb-2">
            <h2 className="text-xl font-bold uppercase flex items-center gap-2">
              <AlertTriangle /> ATTENTION - EXPIRATIONS CRITIQUES
            </h2>
          </div>
          <div className="space-y-2">
            {stats.expiring.critical.map((item, i) => (
              <AlertItem key={i} item={item} />
            ))}
          </div>
        </div>
      )}

      {/* Urgent Alerts */}
      {stats?.expiring?.urgent?.length > 0 && (
        <div className="alert-section">
          <h2 className="text-xl font-bold uppercase mb-2 flex items-center gap-2">
            <Clock className="text-orange-500" /> Urgent (7 jours ou moins)
          </h2>
          <div className="space-y-2">
            {stats.expiring.urgent.map((item, i) => (
              <AlertItem key={i} item={item} />
            ))}
          </div>
        </div>
      )}

      {/* Warning Alerts */}
      {stats?.expiring?.warning?.length > 0 && (
        <div className="alert-section">
          <h2 className="text-xl font-bold uppercase mb-2">À surveiller (15 jours ou moins)</h2>
          <div className="space-y-2">
            {stats.expiring.warning.map((item, i) => (
              <AlertItem key={i} item={item} />
            ))}
          </div>
        </div>
      )}

      {/* Upcoming */}
      {stats?.expiring?.upcoming?.length > 0 && (
        <div className="alert-section">
          <h2 className="text-xl font-bold uppercase mb-2">Prochaines expirations (30 jours)</h2>
          <div className="space-y-2">
            {stats.expiring.upcoming.map((item, i) => (
              <AlertItem key={i} item={item} />
            ))}
          </div>
        </div>
      )}

      {allExpiring.length === 0 && (
        <div className="empty-state">
          <p className="text-xl font-bold">AUCUNE EXPIRATION PROCHE</p>
          <p className="opacity-70 mt-2">Tout est sous contrôle!</p>
        </div>
      )}
    </div>
  );
};

// Generic CRUD Modal
const Modal = ({ isOpen, onClose, title, children }) => {
  if (!isOpen) return null;
  
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold uppercase">{title}</h2>
          <button onClick={onClose} className="btn-secondary p-2" data-testid="modal-close">
            <X size={20} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
};

// Domains Page
const DomainsPage = () => {
  const [domains, setDomains] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    name: '', registrar: '', expiration_date: '', auto_renew: false, notes: '', client_id: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [domainsRes, clientsRes] = await Promise.all([
        axios.get(`${API}/domains`),
        axios.get(`${API}/clients`)
      ]);
      setDomains(domainsRes.data);
      setClients(clientsRes.data);
    } catch (err) {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingItem) {
        await axios.put(`${API}/domains/${editingItem.id}`, formData);
        toast.success('Domaine mis à jour!');
      } else {
        await axios.post(`${API}/domains`, formData);
        toast.success('Domaine ajouté!');
      }
      fetchData();
      closeModal();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce domaine?')) return;
    try {
      await axios.delete(`${API}/domains/${id}`);
      toast.success('Domaine supprimé!');
      fetchData();
    } catch (err) {
      toast.error('Erreur de suppression');
    }
  };

  const openModal = (item = null) => {
    if (item) {
      setEditingItem(item);
      setFormData({ ...item });
    } else {
      setEditingItem(null);
      setFormData({ name: '', registrar: '', expiration_date: '', auto_renew: false, notes: '', client_id: '' });
    }
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingItem(null);
  };

  const getClientName = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    return client?.name || '-';
  };

  if (loading) {
    return <div className="neo-card"><p className="font-bold">CHARGEMENT...</p></div>;
  }

  return (
    <div data-testid="domains-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold uppercase tracking-wider">Domaines</h1>
        <button onClick={() => openModal()} className="btn-primary px-4 py-2 flex items-center gap-2" data-testid="add-domain-btn">
          <Plus size={20} /> AJOUTER
        </button>
      </div>

      {domains.length === 0 ? (
        <div className="empty-state">
          <Globe size={48} className="mx-auto mb-4 opacity-50" />
          <p className="text-xl font-bold">AUCUN DOMAINE</p>
          <p className="opacity-70 mt-2">Ajoutez votre premier domaine</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="neo-table">
            <thead>
              <tr>
                <th>Nom</th>
                <th>Registrar</th>
                <th>Expiration</th>
                <th>Client</th>
                <th>Auto-renouvellement</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {domains.map((domain) => (
                <tr key={domain.id} data-testid={`domain-row-${domain.id}`}>
                  <td className="font-bold">{domain.name}</td>
                  <td>{domain.registrar}</td>
                  <td>{new Date(domain.expiration_date).toLocaleDateString('fr-FR')}</td>
                  <td>{getClientName(domain.client_id)}</td>
                  <td>{domain.auto_renew ? 'OUI' : 'NON'}</td>
                  <td>
                    <div className="flex gap-2">
                      <button onClick={() => openModal(domain)} className="btn-secondary p-2" data-testid={`edit-domain-${domain.id}`}>
                        <Pencil size={16} />
                      </button>
                      <button onClick={() => handleDelete(domain.id)} className="btn-secondary p-2 text-red-600" data-testid={`delete-domain-${domain.id}`}>
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal isOpen={modalOpen} onClose={closeModal} title={editingItem ? 'Modifier le domaine' : 'Ajouter un domaine'}>
        <form onSubmit={handleSubmit} className="space-y-4" data-testid="domain-form">
          <div>
            <label className="block font-bold uppercase text-sm mb-1">Nom du domaine *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full p-3 border-2 border-black bg-white"
              placeholder="exemple.com"
              required
              data-testid="domain-name-input"
            />
          </div>
          <div>
            <label className="block font-bold uppercase text-sm mb-1">Registrar *</label>
            <input
              type="text"
              value={formData.registrar}
              onChange={(e) => setFormData({ ...formData, registrar: e.target.value })}
              className="w-full p-3 border-2 border-black bg-white"
              placeholder="OVH, Gandi, GoDaddy..."
              required
              data-testid="domain-registrar-input"
            />
          </div>
          <div>
            <label className="block font-bold uppercase text-sm mb-1">Date d'expiration *</label>
            <input
              type="date"
              value={formData.expiration_date?.split('T')[0] || ''}
              onChange={(e) => setFormData({ ...formData, expiration_date: e.target.value })}
              className="w-full p-3 border-2 border-black bg-white"
              required
              data-testid="domain-expiration-input"
            />
          </div>
          <div>
            <label className="block font-bold uppercase text-sm mb-1">Client (optionnel)</label>
            <select
              value={formData.client_id}
              onChange={(e) => setFormData({ ...formData, client_id: e.target.value })}
              className="w-full p-3 border-2 border-black bg-white"
              data-testid="domain-client-select"
            >
              <option value="">-- Aucun client --</option>
              {clients.map((client) => (
                <option key={client.id} value={client.id}>{client.name}</option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={formData.auto_renew}
              onChange={(e) => setFormData({ ...formData, auto_renew: e.target.checked })}
              className="w-5 h-5 border-2 border-black"
              data-testid="domain-autorenew-checkbox"
            />
            <label className="font-bold uppercase text-sm">Renouvellement automatique</label>
          </div>
          <div>
            <label className="block font-bold uppercase text-sm mb-1">Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              className="w-full p-3 border-2 border-black bg-white"
              rows={3}
              data-testid="domain-notes-input"
            />
          </div>
          <div className="flex gap-2">
            <button type="submit" className="btn-primary flex-1 p-3" data-testid="domain-submit-btn">
              {editingItem ? 'METTRE À JOUR' : 'AJOUTER'}
            </button>
            <button type="button" onClick={closeModal} className="btn-secondary flex-1 p-3">
              ANNULER
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

// Hostings Page
const HostingsPage = () => {
  const [hostings, setHostings] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    name: '', provider: '', expiration_date: '', plan: '', notes: '', client_id: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [hostingsRes, clientsRes] = await Promise.all([
        axios.get(`${API}/hostings`),
        axios.get(`${API}/clients`)
      ]);
      setHostings(hostingsRes.data);
      setClients(clientsRes.data);
    } catch (err) {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingItem) {
        await axios.put(`${API}/hostings/${editingItem.id}`, formData);
        toast.success('Hébergement mis à jour!');
      } else {
        await axios.post(`${API}/hostings`, formData);
        toast.success('Hébergement ajouté!');
      }
      fetchData();
      closeModal();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cet hébergement?')) return;
    try {
      await axios.delete(`${API}/hostings/${id}`);
      toast.success('Hébergement supprimé!');
      fetchData();
    } catch (err) {
      toast.error('Erreur de suppression');
    }
  };

  const openModal = (item = null) => {
    if (item) {
      setEditingItem(item);
      setFormData({ ...item });
    } else {
      setEditingItem(null);
      setFormData({ name: '', provider: '', expiration_date: '', plan: '', notes: '', client_id: '' });
    }
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingItem(null);
  };

  const getClientName = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    return client?.name || '-';
  };

  if (loading) {
    return <div className="neo-card"><p className="font-bold">CHARGEMENT...</p></div>;
  }

  return (
    <div data-testid="hostings-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold uppercase tracking-wider">Hébergements</h1>
        <button onClick={() => openModal()} className="btn-primary px-4 py-2 flex items-center gap-2" data-testid="add-hosting-btn">
          <Plus size={20} /> AJOUTER
        </button>
      </div>

      {hostings.length === 0 ? (
        <div className="empty-state">
          <Server size={48} className="mx-auto mb-4 opacity-50" />
          <p className="text-xl font-bold">AUCUN HÉBERGEMENT</p>
          <p className="opacity-70 mt-2">Ajoutez votre premier hébergement</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="neo-table">
            <thead>
              <tr>
                <th>Nom</th>
                <th>Fournisseur</th>
                <th>Plan</th>
                <th>Expiration</th>
                <th>Client</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {hostings.map((hosting) => (
                <tr key={hosting.id} data-testid={`hosting-row-${hosting.id}`}>
                  <td className="font-bold">{hosting.name}</td>
                  <td>{hosting.provider}</td>
                  <td>{hosting.plan || '-'}</td>
                  <td>{new Date(hosting.expiration_date).toLocaleDateString('fr-FR')}</td>
                  <td>{getClientName(hosting.client_id)}</td>
                  <td>
                    <div className="flex gap-2">
                      <button onClick={() => openModal(hosting)} className="btn-secondary p-2" data-testid={`edit-hosting-${hosting.id}`}>
                        <Pencil size={16} />
                      </button>
                      <button onClick={() => handleDelete(hosting.id)} className="btn-secondary p-2 text-red-600" data-testid={`delete-hosting-${hosting.id}`}>
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal isOpen={modalOpen} onClose={closeModal} title={editingItem ? 'Modifier l\'hébergement' : 'Ajouter un hébergement'}>
        <form onSubmit={handleSubmit} className="space-y-4" data-testid="hosting-form">
          <div>
            <label className="block font-bold uppercase text-sm mb-1">Nom *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full p-3 border-2 border-black bg-white"
              placeholder="Mon hébergement"
              required
              data-testid="hosting-name-input"
            />
          </div>
          <div>
            <label className="block font-bold uppercase text-sm mb-1">Fournisseur *</label>
            <input
              type="text"
              value={formData.provider}
              onChange={(e) => setFormData({ ...formData, provider: e.target.value })}
              className="w-full p-3 border-2 border-black bg-white"
              placeholder="OVH, AWS, DigitalOcean..."
              required
              data-testid="hosting-provider-input"
            />
          </div>
          <div>
            <label className="block font-bold uppercase text-sm mb-1">Plan (optionnel)</label>
            <input
              type="text"
              value={formData.plan}
              onChange={(e) => setFormData({ ...formData, plan: e.target.value })}
              className="w-full p-3 border-2 border-black bg-white"
              placeholder="Pro, Business, Enterprise..."
              data-testid="hosting-plan-input"
            />
          </div>
          <div>
            <label className="block font-bold uppercase text-sm mb-1">Date d'expiration *</label>
            <input
              type="date"
              value={formData.expiration_date?.split('T')[0] || ''}
              onChange={(e) => setFormData({ ...formData, expiration_date: e.target.value })}
              className="w-full p-3 border-2 border-black bg-white"
              required
              data-testid="hosting-expiration-input"
            />
          </div>
          <div>
            <label className="block font-bold uppercase text-sm mb-1">Client (optionnel)</label>
            <select
              value={formData.client_id}
              onChange={(e) => setFormData({ ...formData, client_id: e.target.value })}
              className="w-full p-3 border-2 border-black bg-white"
              data-testid="hosting-client-select"
            >
              <option value="">-- Aucun client --</option>
              {clients.map((client) => (
                <option key={client.id} value={client.id}>{client.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block font-bold uppercase text-sm mb-1">Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              className="w-full p-3 border-2 border-black bg-white"
              rows={3}
              data-testid="hosting-notes-input"
            />
          </div>
          <div className="flex gap-2">
            <button type="submit" className="btn-primary flex-1 p-3" data-testid="hosting-submit-btn">
              {editingItem ? 'METTRE À JOUR' : 'AJOUTER'}
            </button>
            <button type="button" onClick={closeModal} className="btn-secondary flex-1 p-3">
              ANNULER
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

// Subscriptions Page
const SubscriptionsPage = () => {
  const [subscriptions, setSubscriptions] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    name: '', provider: '', expiration_date: '', type: 'internal', price: '', currency: 'EUR', notes: '', client_id: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [subscriptionsRes, clientsRes] = await Promise.all([
        axios.get(`${API}/subscriptions`),
        axios.get(`${API}/clients`)
      ]);
      setSubscriptions(subscriptionsRes.data);
      setClients(clientsRes.data);
    } catch (err) {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = { ...formData, price: formData.price ? parseFloat(formData.price) : null };
      if (editingItem) {
        await axios.put(`${API}/subscriptions/${editingItem.id}`, data);
        toast.success('Abonnement mis à jour!');
      } else {
        await axios.post(`${API}/subscriptions`, data);
        toast.success('Abonnement ajouté!');
      }
      fetchData();
      closeModal();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cet abonnement?')) return;
    try {
      await axios.delete(`${API}/subscriptions/${id}`);
      toast.success('Abonnement supprimé!');
      fetchData();
    } catch (err) {
      toast.error('Erreur de suppression');
    }
  };

  const openModal = (item = null) => {
    if (item) {
      setEditingItem(item);
      setFormData({ ...item, price: item.price || '' });
    } else {
      setEditingItem(null);
      setFormData({ name: '', provider: '', expiration_date: '', type: 'internal', price: '', currency: 'EUR', notes: '', client_id: '' });
    }
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingItem(null);
  };

  const getClientName = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    return client?.name || '-';
  };

  if (loading) {
    return <div className="neo-card"><p className="font-bold">CHARGEMENT...</p></div>;
  }

  return (
    <div data-testid="subscriptions-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold uppercase tracking-wider">Abonnements</h1>
        <button onClick={() => openModal()} className="btn-primary px-4 py-2 flex items-center gap-2" data-testid="add-subscription-btn">
          <Plus size={20} /> AJOUTER
        </button>
      </div>

      {subscriptions.length === 0 ? (
        <div className="empty-state">
          <CreditCard size={48} className="mx-auto mb-4 opacity-50" />
          <p className="text-xl font-bold">AUCUN ABONNEMENT</p>
          <p className="opacity-70 mt-2">Ajoutez votre premier abonnement</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="neo-table">
            <thead>
              <tr>
                <th>Nom</th>
                <th>Fournisseur</th>
                <th>Type</th>
                <th>Prix</th>
                <th>Expiration</th>
                <th>Client</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {subscriptions.map((sub) => (
                <tr key={sub.id} data-testid={`subscription-row-${sub.id}`}>
                  <td className="font-bold">{sub.name}</td>
                  <td>{sub.provider}</td>
                  <td><span className={sub.type === 'client' ? 'badge-safe' : 'badge-warning'}>{sub.type === 'client' ? 'Client' : 'Interne'}</span></td>
                  <td>{sub.price ? `${sub.price} ${sub.currency}` : '-'}</td>
                  <td>{new Date(sub.expiration_date).toLocaleDateString('fr-FR')}</td>
                  <td>{getClientName(sub.client_id)}</td>
                  <td>
                    <div className="flex gap-2">
                      <button onClick={() => openModal(sub)} className="btn-secondary p-2" data-testid={`edit-subscription-${sub.id}`}>
                        <Pencil size={16} />
                      </button>
                      <button onClick={() => handleDelete(sub.id)} className="btn-secondary p-2 text-red-600" data-testid={`delete-subscription-${sub.id}`}>
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal isOpen={modalOpen} onClose={closeModal} title={editingItem ? 'Modifier l\'abonnement' : 'Ajouter un abonnement'}>
        <form onSubmit={handleSubmit} className="space-y-4" data-testid="subscription-form">
          <div>
            <label className="block font-bold uppercase text-sm mb-1">Nom *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full p-3 border-2 border-black bg-white"
              placeholder="Netflix, Slack, GitHub..."
              required
              data-testid="subscription-name-input"
            />
          </div>
          <div>
            <label className="block font-bold uppercase text-sm mb-1">Fournisseur *</label>
            <input
              type="text"
              value={formData.provider}
              onChange={(e) => setFormData({ ...formData, provider: e.target.value })}
              className="w-full p-3 border-2 border-black bg-white"
              required
              data-testid="subscription-provider-input"
            />
          </div>
          <div>
            <label className="block font-bold uppercase text-sm mb-1">Type *</label>
            <select
              value={formData.type}
              onChange={(e) => setFormData({ ...formData, type: e.target.value })}
              className="w-full p-3 border-2 border-black bg-white"
              data-testid="subscription-type-select"
            >
              <option value="internal">Interne (pour vous)</option>
              <option value="client">Client (vendu à un client)</option>
            </select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block font-bold uppercase text-sm mb-1">Prix</label>
              <input
                type="number"
                step="0.01"
                value={formData.price}
                onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                className="w-full p-3 border-2 border-black bg-white"
                data-testid="subscription-price-input"
              />
            </div>
            <div>
              <label className="block font-bold uppercase text-sm mb-1">Devise</label>
              <select
                value={formData.currency}
                onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                className="w-full p-3 border-2 border-black bg-white"
                data-testid="subscription-currency-select"
              >
                <option value="EUR">EUR</option>
                <option value="USD">USD</option>
                <option value="XOF">XOF</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block font-bold uppercase text-sm mb-1">Date d'expiration *</label>
            <input
              type="date"
              value={formData.expiration_date?.split('T')[0] || ''}
              onChange={(e) => setFormData({ ...formData, expiration_date: e.target.value })}
              className="w-full p-3 border-2 border-black bg-white"
              required
              data-testid="subscription-expiration-input"
            />
          </div>
          <div>
            <label className="block font-bold uppercase text-sm mb-1">Client (optionnel)</label>
            <select
              value={formData.client_id}
              onChange={(e) => setFormData({ ...formData, client_id: e.target.value })}
              className="w-full p-3 border-2 border-black bg-white"
              data-testid="subscription-client-select"
            >
              <option value="">-- Aucun client --</option>
              {clients.map((client) => (
                <option key={client.id} value={client.id}>{client.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block font-bold uppercase text-sm mb-1">Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              className="w-full p-3 border-2 border-black bg-white"
              rows={3}
              data-testid="subscription-notes-input"
            />
          </div>
          <div className="flex gap-2">
            <button type="submit" className="btn-primary flex-1 p-3" data-testid="subscription-submit-btn">
              {editingItem ? 'METTRE À JOUR' : 'AJOUTER'}
            </button>
            <button type="button" onClick={closeModal} className="btn-secondary flex-1 p-3">
              ANNULER
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

// Clients Page
const ClientsPage = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', company: '', notes: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const res = await axios.get(`${API}/clients`);
      setClients(res.data);
    } catch (err) {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingItem) {
        await axios.put(`${API}/clients/${editingItem.id}`, formData);
        toast.success('Client mis à jour!');
      } else {
        await axios.post(`${API}/clients`, formData);
        toast.success('Client ajouté!');
      }
      fetchData();
      closeModal();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce client?')) return;
    try {
      await axios.delete(`${API}/clients/${id}`);
      toast.success('Client supprimé!');
      fetchData();
    } catch (err) {
      toast.error('Erreur de suppression');
    }
  };

  const openModal = (item = null) => {
    if (item) {
      setEditingItem(item);
      setFormData({ ...item });
    } else {
      setEditingItem(null);
      setFormData({ name: '', email: '', phone: '', company: '', notes: '' });
    }
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingItem(null);
  };

  if (loading) {
    return <div className="neo-card"><p className="font-bold">CHARGEMENT...</p></div>;
  }

  return (
    <div data-testid="clients-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold uppercase tracking-wider">Clients</h1>
        <button onClick={() => openModal()} className="btn-primary px-4 py-2 flex items-center gap-2" data-testid="add-client-btn">
          <Plus size={20} /> AJOUTER
        </button>
      </div>

      {clients.length === 0 ? (
        <div className="empty-state">
          <Users size={48} className="mx-auto mb-4 opacity-50" />
          <p className="text-xl font-bold">AUCUN CLIENT</p>
          <p className="opacity-70 mt-2">Ajoutez votre premier client</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="neo-table">
            <thead>
              <tr>
                <th>Nom</th>
                <th>Email</th>
                <th>Téléphone</th>
                <th>Entreprise</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {clients.map((client) => (
                <tr key={client.id} data-testid={`client-row-${client.id}`}>
                  <td className="font-bold">{client.name}</td>
                  <td>{client.email || '-'}</td>
                  <td>{client.phone || '-'}</td>
                  <td>{client.company || '-'}</td>
                  <td>
                    <div className="flex gap-2">
                      <button onClick={() => openModal(client)} className="btn-secondary p-2" data-testid={`edit-client-${client.id}`}>
                        <Pencil size={16} />
                      </button>
                      <button onClick={() => handleDelete(client.id)} className="btn-secondary p-2 text-red-600" data-testid={`delete-client-${client.id}`}>
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal isOpen={modalOpen} onClose={closeModal} title={editingItem ? 'Modifier le client' : 'Ajouter un client'}>
        <form onSubmit={handleSubmit} className="space-y-4" data-testid="client-form">
          <div>
            <label className="block font-bold uppercase text-sm mb-1">Nom *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full p-3 border-2 border-black bg-white"
              required
              data-testid="client-name-input"
            />
          </div>
          <div>
            <label className="block font-bold uppercase text-sm mb-1">Email</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="w-full p-3 border-2 border-black bg-white"
              data-testid="client-email-input"
            />
          </div>
          <div>
            <label className="block font-bold uppercase text-sm mb-1">Téléphone</label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              className="w-full p-3 border-2 border-black bg-white"
              data-testid="client-phone-input"
            />
          </div>
          <div>
            <label className="block font-bold uppercase text-sm mb-1">Entreprise</label>
            <input
              type="text"
              value={formData.company}
              onChange={(e) => setFormData({ ...formData, company: e.target.value })}
              className="w-full p-3 border-2 border-black bg-white"
              data-testid="client-company-input"
            />
          </div>
          <div>
            <label className="block font-bold uppercase text-sm mb-1">Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              className="w-full p-3 border-2 border-black bg-white"
              rows={3}
              data-testid="client-notes-input"
            />
          </div>
          <div className="flex gap-2">
            <button type="submit" className="btn-primary flex-1 p-3" data-testid="client-submit-btn">
              {editingItem ? 'METTRE À JOUR' : 'AJOUTER'}
            </button>
            <button type="button" onClick={closeModal} className="btn-secondary flex-1 p-3">
              ANNULER
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

// Settings Page
const SettingsPage = () => {
  const { user } = useAuth();
  const [settings, setSettings] = useState({
    discord_webhook: '',
    whatsapp_number: '',
    notify_30_days: true,
    notify_15_days: true,
    notify_7_days: true,
    notify_day_of: true
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notificationPermission, setNotificationPermission] = useState('default');

  useEffect(() => {
    fetchSettings();
    if ('Notification' in window) {
      setNotificationPermission(Notification.permission);
    }
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/settings/notifications`);
      setSettings(res.data);
    } catch (err) {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings/notifications`, settings);
      toast.success('Paramètres sauvegardés!');
    } catch (err) {
      toast.error('Erreur de sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const requestNotificationPermission = async () => {
    try {
      const result = await Notification.requestPermission();
      setNotificationPermission(result);
      if (result === 'granted') {
        toast.success('Notifications activées!');
        // Test notification
        new Notification('SubManager', {
          body: 'Les notifications sont maintenant activées!',
          icon: '/icon-192.png'
        });
      }
    } catch (err) {
      toast.error('Erreur d\'activation des notifications');
    }
  };

  const testNotification = () => {
    if (notificationPermission === 'granted') {
      new Notification('Test SubManager', {
        body: 'Ceci est une notification de test. Vous recevrez des alertes avant expiration!',
        icon: '/icon-192.png',
        badge: '/icon-72.png'
      });
      toast.success('Notification envoyée!');
    }
  };

  if (loading) {
    return <div className="neo-card"><p className="font-bold">CHARGEMENT...</p></div>;
  }

  return (
    <div data-testid="settings-page">
      <h1 className="text-3xl font-bold mb-6 uppercase tracking-wider">Paramètres</h1>

      <div className="neo-card mb-6">
        <h2 className="text-xl font-bold mb-4 uppercase">Profil</h2>
        <div className="space-y-2">
          <p><span className="font-bold">Nom:</span> {user?.name}</p>
          <p><span className="font-bold">Email:</span> {user?.email}</p>
        </div>
      </div>

      {/* PWA & Mobile Section */}
      <div className="neo-card mb-6">
        <h2 className="text-xl font-bold mb-4 uppercase flex items-center gap-2">
          <Smartphone size={24} /> Application Mobile (PWA)
        </h2>
        
        <div className="space-y-4">
          <div className="p-4 bg-gray-100 border-2 border-black">
            <p className="font-bold mb-2">Installer l'application sur votre téléphone:</p>
            <ol className="list-decimal list-inside space-y-1 text-sm">
              <li><strong>iPhone/iPad:</strong> Safari → Partager → "Sur l'écran d'accueil"</li>
              <li><strong>Android:</strong> Chrome → Menu (⋮) → "Installer l'application"</li>
              <li><strong>PC/Mac:</strong> Chrome → Barre d'adresse → Icône d'installation</li>
            </ol>
          </div>

          <div className="border-t-2 border-black pt-4">
            <h3 className="font-bold uppercase mb-3 flex items-center gap-2">
              <Bell size={20} /> Notifications Push
            </h3>
            
            {notificationPermission === 'granted' ? (
              <div className="flex items-center gap-4">
                <span className="badge-safe">ACTIVÉES</span>
                <button onClick={testNotification} className="btn-secondary px-3 py-2 text-sm">
                  TESTER
                </button>
              </div>
            ) : notificationPermission === 'denied' ? (
              <div>
                <span className="badge-critical">BLOQUÉES</span>
                <p className="text-sm mt-2 opacity-70">Activez les notifications dans les paramètres de votre navigateur</p>
              </div>
            ) : (
              <button onClick={requestNotificationPermission} className="btn-primary px-4 py-2" data-testid="enable-push-btn">
                ACTIVER LES NOTIFICATIONS
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="neo-card mb-6">
        <h2 className="text-xl font-bold mb-4 uppercase">Notifications Externes</h2>
        
        <div className="space-y-4">
          <div>
            <label className="block font-bold uppercase text-sm mb-1">Discord Webhook URL</label>
            <input
              type="url"
              value={settings.discord_webhook || ''}
              onChange={(e) => setSettings({ ...settings, discord_webhook: e.target.value })}
              className="w-full p-3 border-2 border-black bg-white"
              placeholder="https://discord.com/api/webhooks/..."
              data-testid="discord-webhook-input"
            />
            <p className="text-xs mt-1 opacity-70">Créez un webhook dans les paramètres de votre serveur Discord</p>
          </div>

          <div>
            <label className="block font-bold uppercase text-sm mb-1">Numéro WhatsApp</label>
            <input
              type="tel"
              value={settings.whatsapp_number || ''}
              onChange={(e) => setSettings({ ...settings, whatsapp_number: e.target.value })}
              className="w-full p-3 border-2 border-black bg-white"
              placeholder="+33612345678"
              data-testid="whatsapp-number-input"
            />
            <p className="text-xs mt-1 opacity-70">Format international avec le code pays</p>
          </div>

          <div className="border-t-2 border-black pt-4 mt-4">
            <h3 className="font-bold uppercase mb-3">Quand recevoir les alertes?</h3>
            <div className="space-y-2">
              {[
                { key: 'notify_30_days', label: '30 jours avant expiration' },
                { key: 'notify_15_days', label: '15 jours avant expiration' },
                { key: 'notify_7_days', label: '7 jours avant expiration' },
                { key: 'notify_day_of', label: 'Le jour de l\'expiration' }
              ].map((item) => (
                <label key={item.key} className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings[item.key]}
                    onChange={(e) => setSettings({ ...settings, [item.key]: e.target.checked })}
                    className="w-5 h-5 border-2 border-black"
                    data-testid={`notify-${item.key}`}
                  />
                  <span className="font-bold">{item.label}</span>
                </label>
              ))}
            </div>
          </div>
        </div>

        <button
          onClick={handleSave}
          disabled={saving}
          className="btn-primary w-full p-3 mt-6"
          data-testid="save-settings-btn"
        >
          {saving ? 'SAUVEGARDE...' : 'SAUVEGARDER LES PARAMÈTRES'}
        </button>
      </div>
    </div>
  );
};

// Register Service Worker
const registerServiceWorker = async () => {
  if ('serviceWorker' in navigator) {
    try {
      const registration = await navigator.serviceWorker.register('/service-worker.js');
      console.log('Service Worker registered:', registration);
    } catch (error) {
      console.error('Service Worker registration failed:', error);
    }
  }
};

// Main App
function App() {
  useEffect(() => {
    registerServiceWorker();
  }, []);

  return (
    <AuthProvider>
      <div className="app-container">
        <Toaster position="top-right" richColors />
        <NotificationPermission />
        <PWAInstallBanner />
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <DashboardLayout><DashboardPage /></DashboardLayout>
              </ProtectedRoute>
            } />
            <Route path="/domains" element={
              <ProtectedRoute>
                <DashboardLayout><DomainsPage /></DashboardLayout>
              </ProtectedRoute>
            } />
            <Route path="/hostings" element={
              <ProtectedRoute>
                <DashboardLayout><HostingsPage /></DashboardLayout>
              </ProtectedRoute>
            } />
            <Route path="/subscriptions" element={
              <ProtectedRoute>
                <DashboardLayout><SubscriptionsPage /></DashboardLayout>
              </ProtectedRoute>
            } />
            <Route path="/clients" element={
              <ProtectedRoute>
                <DashboardLayout><ClientsPage /></DashboardLayout>
              </ProtectedRoute>
            } />
            <Route path="/settings" element={
              <ProtectedRoute>
                <DashboardLayout><SettingsPage /></DashboardLayout>
              </ProtectedRoute>
            } />
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </BrowserRouter>
      </div>
    </AuthProvider>
  );
}

export default App;
